import { useContext, useState, useEffect } from "react";
import { PluginContext } from "./context";
import { NavigationItem, PluginContextType } from "./types";
import { frontendSubscriptionService } from "@foundation/services/frontendSubscriptionService";
import { useUser } from "@foundation/contextapi/UserContext";
import { useUserRole } from "@foundation/hooks/useUserRole";

export const usePluginContext = (): PluginContextType => {
  const context = useContext(PluginContext);
  if (!context) {
    throw new Error("usePluginContext must be used within a PluginProvider");
  }
  return context;
};

export const usePluginNavigation = () => {
  const { loadedPlugins } = usePluginContext();
  const { user } = useUser(); // 🔥 Add user dependency
  const { isAdmin, isLoading: roleLoading } = useUserRole(); // 🔥 Add admin role checking and loading state
  const [navigation, setNavigation] = useState<NavigationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadNavigation = async () => {
      try {
        setIsLoading(true);
        console.log("🧭 Loading navigation from plugins...");
        console.log("🔍 User state:", !!user);
        console.log("🔍 Admin state:", isAdmin);
        console.log("🔍 Role loading:", roleLoading);
        console.log("🔍 Available loaded plugins:", Object.keys(loadedPlugins));
        console.log(
          "🔍 Total loaded plugins count:",
          Object.keys(loadedPlugins).length
        );

        const navItems: NavigationItem[] = [];

        for (const [pluginName, plugin] of Object.entries(loadedPlugins)) {
          console.log(`🔍 Checking plugin ${pluginName}:`, plugin);
          console.log(`🔍 Plugin ${pluginName} type:`, typeof plugin);
          console.log(
            `🔍 Plugin ${pluginName} has navigation:`,
            "navigation" in plugin
          );

          if (plugin && typeof plugin === "object" && "navigation" in plugin) {
            const pluginModule = plugin as any;
            console.log(
              `🔍 Plugin ${pluginName} navigation:`,
              pluginModule.navigation
            );
            console.log(
              `🔍 Plugin ${pluginName} navigation is array:`,
              Array.isArray(pluginModule.navigation)
            );

            if (
              pluginModule.navigation &&
              Array.isArray(pluginModule.navigation)
            ) {
              // Check feature access for each navigation item
              for (const navItem of pluginModule.navigation) {
                let hasAccess = true;

                // Check for admin-only features (FormEngine + Knowledge Base)
                if (
                  navItem.permissions?.includes('admin') ||
                  navItem.name === "FormEngine" ||
                  navItem.route?.startsWith("/formengine") ||
                  navItem.name === "Knowledge Base" ||
                  navItem.route?.startsWith("/knowledge")
                ) {
                  hasAccess = isAdmin;
                  console.log(
                    `🔐 Admin access check for ${navItem.name}: ${hasAccess}`
                  );
                }
                // Check for revisr feature access
                else if (
                  navItem.name === "Revisr" ||
                  navItem.route?.startsWith("/revisr")
                ) {
                  try {
                    hasAccess =
                      await frontendSubscriptionService.hasFeatureAccess(
                        "revisr"
                      );
                    console.log(
                      `🔐 Revisr feature access check: ${hasAccess}`
                    );
                  } catch (error) {
                    console.error(
                      "Error checking revisr feature access:",
                      error
                    );
                    hasAccess = false;
                  }
                }

                if (hasAccess) {
                  console.log(`✅ Adding navigation item: ${navItem.name}`);
                  navItems.push(navItem);
                } else {
                  console.log(
                    `🚫 Skipping navigation item due to feature access: ${navItem.name}`
                  );
                }
              }
            } else {
              console.log(`❌ No navigation array found for ${pluginName}`);
            }
          } else {
            console.log(`❌ No navigation property found for ${pluginName}`);
          }
        }

        // Sort by order if specified
        navItems.sort((a, b) => (a.order || 999) - (b.order || 999));
        console.log("🧭 Final navigation items:", navItems);
        console.log("🧭 Final navigation items count:", navItems.length);
        setNavigation(navItems);
      } catch (err) {
        console.error("Failed to load navigation:", err);
      } finally {
        setIsLoading(false);
      }
    };

    // 🔥 Only load navigation if user is logged in and role is loaded
    if (user && !roleLoading) {
      loadNavigation();
    } else if (!user) {
      // Clear navigation when user logs out
      setNavigation([]);
      setIsLoading(false);
    }
    // If roleLoading is true, wait for it to complete before loading navigation
  }, [loadedPlugins, user, isAdmin, roleLoading]); // 🔥 Add isAdmin and roleLoading dependencies

  return { navigation, isLoading };
};

export const usePluginRoutes = () => {
  const { loadedPlugins } = usePluginContext();
  const routes = Object.values(loadedPlugins).flatMap((plugin) =>
    plugin && typeof plugin === "object" && "routes" in plugin
      ? (plugin as any).routes || []
      : []
  );
  return { routes };
};
